import React from 'react';
import type { ComponentProps, ReactElement } from 'react';

import PropTypes from 'prop-types';

import {
  useControlled,
  variantSwitch,
  getValidChildren,
} from '@adsk/alloy-react-helpers';
import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import Button from '../Button';

const UNSET: unknown = 'unset';

const VARIANTS = {
  SECONDARY: Button.VARIANTS.SECONDARY,
  TERTIARY: Button.VARIANTS.TERTIARY,
} as const;

const SIZES = {
  SMALL: Button.SIZES.SMALL,
  MEDIUM: Button.SIZES.MEDIUM,
} as const;

/**
 * ButtonGroup used for grouping buttons
 * we want to group together and manage their selected state
 */
const ButtonGroup = <T,>({
  className,
  style,
  children,
  variant = VARIANTS.SECONDARY,
  size = SIZES.MEDIUM,
  disabled,
  value,
  onChange,
  ...props
}: Omit<StylableComponent<HTMLDivElement>, 'onChange' | 'value'> & {
  variant?: (typeof VARIANTS)[keyof typeof VARIANTS];
  size?: (typeof SIZES)[keyof typeof SIZES];
  disabled?: boolean;
  value?: T;
  onChange?: (newValue: T) => void;
}) => {
  const [selected, setSelected] = useControlled(UNSET as T, value, onChange);

  // if has only one button return button instead of ButtonGroup
  if (React.Children.count(children) === 1) {
    return children as ReactElement;
  }

  return (
    <div
      role={'group'}
      className={className}
      css={[
        {
          display: 'inline-flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: theme.borderRadius.small,
        },

        variantSwitch({
          statement: variant,
          variants: {
            [VARIANTS.SECONDARY]: {
              transition: theme.transitions.create(['box-shadow', 'border']),
              '&:hover': {
                boxShadow: theme.halos.hover,
              },
              '& > button:first-child': {
                borderRadius: `${theme.borderRadius.small}px ${theme.borderRadius.none} ${theme.borderRadius.none} ${theme.borderRadius.small}px`,
              },
              '& > button:not(:first-child):not(:last-child)': {
                borderRadius: theme.borderRadius.none,
              },
              '& > button:last-child': {
                borderRadius: `${theme.borderRadius.none} ${theme.borderRadius.small}px ${theme.borderRadius.small}px ${theme.borderRadius.none}`,
              },
            },
            [VARIANTS.TERTIARY]: {
              '& > button:first-child': {
                borderRadius: `${theme.borderRadius.small}px ${theme.borderRadius.none} ${theme.borderRadius.none} ${theme.borderRadius.small}px`,
                borderRight: `1px solid ${theme.tokens.colors.border.subtle.value}`,
              },
              '& > button:not(:first-child):not(:last-child)': {
                borderRadius: theme.borderRadius.none,
                borderRight: `1px solid ${theme.tokens.colors.border.subtle.value}`,
              },
              '& > button:last-child': {
                borderRadius: `${theme.borderRadius.none} ${theme.borderRadius.small}px ${theme.borderRadius.small}px ${theme.borderRadius.none}`,
              },
              '& > button': {
                zIndex: 1,
              },
              '&:hover': {
                button: {
                  borderRightColor: theme.tokens.colors.border.inverse.value,
                },
                '& > button:not(:first-child):not(:last-child)': {
                  borderRightColor: theme.tokens.colors.border.inverse.value,
                },
              },
              ...(selected !== UNSET && {
                button: {
                  borderRightColor: theme.tokens.colors.border.inverse.value,
                },
              }),
            },
          },
        }),

        style,
      ]}
      {...props}
    >
      {React.Children.map(getValidChildren(children), (child, position) => {
        const typedChild = child as ReactElement<ComponentProps<typeof Button>>;
        const childProps = child.props as ComponentProps<typeof Button>;

        const selectionValue = (childProps?.value || position) as T;

        return React.cloneElement(typedChild, {
          ...childProps,
          selected: selected === selectionValue,
          onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            if (childProps.onClick) {
              childProps.onClick(e);
            }

            setSelected(selectionValue);
          },
          variant,
          size,
          disabled: disabled || childProps.disabled,
          style: [
            {
              '&:hover': {
                boxShadow: 'none',
                ...(selected !== selectionValue && {
                  background:
                    theme.tokens.colors.background.neutral.hover.value,
                }),
              },
              '&:focus': {
                boxShadow: theme.halos.focus,
                zIndex: 10,
              },
              '&:active': {
                boxShadow: theme.halos.active,
              },
              '&[disabled]': {
                '&:hover': {
                  background: theme.tokens.colors.background.primary.value,
                },
              },
            },
            variantSwitch({
              statement: variant,
              variants: {
                [VARIANTS.SECONDARY]: {
                  '&[disabled]': {
                    borderColor: theme.tokens.colors.border.value,
                    color: theme.tokens.colors.text.inactive.value,
                    svg: {
                      color: theme.tokens.colors.icon.inactive.value,
                    },
                  },
                },
                [VARIANTS.TERTIARY]: {
                  '&[disabled]': {
                    color: theme.tokens.colors.text.inactive.value,
                    svg: {
                      color: theme.tokens.colors.icon.inactive.value,
                    },
                  },
                },
              },
            }),
            childProps.style,
          ],
        });
      })}
    </div>
  );
};

ButtonGroup.VARIANTS = VARIANTS;
ButtonGroup.SIZES = SIZES;
ButtonGroup.displayName = 'ButtonGroup';

ButtonGroup.propTypes = {
  /**  The component's children tabs */
  children: PropTypes.any,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /**  The style variant of buttons, defaults to 'secondary' */
  variant: PropTypes.oneOf(Object.values(VARIANTS)),
  /**  The size of buttons, defaults to 'medium' */
  size: PropTypes.oneOf(Object.values(SIZES)),
  /** Disable buttons functionality */
  disabled: PropTypes.bool,
  /** value for controlled usage */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** onChange handler for controlled usage */
  onChange: PropTypes.func,
};

export default ButtonGroup;
